import React from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import VoiceInputButton from '../../../components/ui/VoiceInputButton';
import { sanitizeInput } from '../../../utils/sanitizer';

const MedicationTracker = ({ medicationStatus, onSelect, medicationDetails, onDetailsChange }) => {
  const medicationOptions = [
    { id: 'took-usual', label: 'Took Usual', icon: 'Check', color: 'text-success' },
    { id: 'took-extra', label: 'Took Extra', icon: 'Plus', color: 'text-warning' },
    { id: 'missed', label: 'Missed', icon: 'X', color: 'text-error' },
    { id: 'started-today', label: 'Started Medicating Today', icon: 'Play', color: 'text-info' },
    { id: 'no-medication', label: 'No Medication Today', icon: 'Minus', color: 'text-muted-foreground' }
  ];

  const handleDetailsChange = (e) => {
    const rawValue = e?.target?.value;
    const sanitized = sanitizeInput(rawValue, {
      maxLength: 500,
      allowHtml: false,
      removeControlChars: false
    });
    onDetailsChange?.(sanitized);
  };

  const handleVoiceChange = (text) => {
    onDetailsChange?.(text);
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg md:text-xl lg:text-2xl font-heading font-bold text-foreground mb-2">
          Medication Status
        </h3>
        <p className="text-sm md:text-base text-muted-foreground">
          Let us know about your medication today
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {medicationOptions?.map((option) => {
          const isSelected = medicationStatus === option?.id;
          return (
            <button
              key={option?.id}
              onClick={() => onSelect(option?.id)}
              className={`
                flex flex-col items-center justify-center p-4 md:p-5 rounded-xl
                border-2 transition-smooth min-h-[100px] md:min-h-[120px]
                ${isSelected 
                  ? 'border-primary bg-primary/5 shadow-therapeutic-md' 
                  : 'border-border bg-card hover:border-primary/30 hover:shadow-therapeutic'
                }
                focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background
                active:scale-[0.97]
              `}
              aria-pressed={isSelected}
              aria-label={option?.label}
            >
              <div className={`
                flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-lg mb-3
                ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}
                transition-smooth
              `}>
                <Icon name={option?.icon} size={24} strokeWidth={2} />
              </div>
              <span className={`
                text-sm md:text-base font-medium text-center
                ${isSelected ? 'text-foreground' : 'text-muted-foreground'}
              `}>
                {option?.label}
              </span>
            </button>
          );
        })}
      </div>
      {/* Medication Details Input */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="medication-details" className="block text-sm md:text-base font-medium text-foreground">
            Medication Details <span className="text-muted-foreground">(Optional)</span>
          </label>
          <VoiceInputButton
            value={medicationDetails || ''}
            onChange={handleVoiceChange}
            maxLength={500}
          />
        </div>
        <p className="text-xs md:text-sm text-muted-foreground mb-3">
          Enter the name and dosage of medication taken, if any
        </p>
        <Input
          id="medication-details"
          type="text"
          placeholder="e.g., Ibuprofen 200mg, Aspirin 81mg"
          value={medicationDetails}
          onChange={handleDetailsChange}
          maxLength={500}
          className="w-full"
        />
        <p className="text-xs text-muted-foreground text-right mt-2">
          {(medicationDetails?.length || 0)}/500
        </p>
      </div>
    </div>
  );
};

export default MedicationTracker;